import React, { createContext, useContext, useEffect, useMemo } from "react";
import { notification } from "antd";

export type ToastKind = "info" | "success" | "error" | "warn";

export interface ToastAction {
  label: string;
  onClick?: () => void;
}

export interface ToastInput {
  kind?: ToastKind;
  title: string;
  desc?: string;
  duration?: number;
  actions?: ToastAction[];
}

interface ToastCtxValue {
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastCtx = createContext<ToastCtxValue | null>(null);

export const useToast = (): ToastCtxValue => {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
};

// Map our ToastKind → AntD notification type. "warn" → AntD's "warning".
const AND_TYPE: Record<ToastKind, "success" | "error" | "info" | "warning"> = {
  success: "success",
  error: "error",
  info: "info",
  warn: "warning",
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  useEffect(() => {
    notification.config({ placement: "topRight", top: 76, duration: 4.5 });
  }, []);

  const value = useMemo<ToastCtxValue>(() => {
    const push = (input: ToastInput): string => {
      const kind = input.kind || "info";
      const key = Math.random().toString(36).slice(2);
      const type = AND_TYPE[kind];
      const durationSec =
        input.duration === undefined ? 4.5 : input.duration / 1000;

      const btn = input.actions && input.actions.length > 0 && (
        <span>
          {input.actions.map((action, index) => (
            <button
              key={index}
              type="button"
              className="ant-btn ant-btn-link"
              style={{ paddingLeft: 0, paddingRight: 12 }}
              onClick={() => {
                action.onClick && action.onClick();
                notification.close(key);
              }}
            >
              {action.label}
            </button>
          ))}
        </span>
      );

      notification[type]({
        key,
        message: input.title,
        description: input.desc,
        duration: durationSec,
        btn: btn || undefined,
      });
      return key;
    };

    const dismiss = (id: string): void => {
      notification.close(id);
    };

    return { push, dismiss };
  }, []);

  return <ToastCtx.Provider value={value}>{children}</ToastCtx.Provider>;
};
